import { defineComponent, ref, PropType, watch, onMounted } from "vue";

import { useLocalization } from "@/providers/Localization";

import { getStageTooltip } from "@/services/Views/MaterialDatabase/StageTooltip";

import { Material } from "@/types/Views/MaterialDatabase/Material";
import { resolveIndicatorType } from "@/utils/constructionMaterial";

import EmissionByMaterialLifecycleStageChart from "@/features/Inspect/components/EmissionByMaterialLifecycleStageChart";
import { InspectEmissionByStagesType } from "@/features/Inspect";

const CompareGraph = defineComponent({
  props: {
    materials: {
      type: Array as PropType<Material[]>,
      required: true,
    },
    chartTitle: {
      type: String,
      default: "",
    },
    indicatorUnit: {
      type: String,
      default: "",
    },
    selectedIndicator: {
      type: Number,
      required: true,
    },
    selectedStages: {
      type: Array as PropType<number[]>,
      default: undefined,
    },
  },

  setup(props) {
    const localization = useLocalization();

    const chartData = ref<InspectEmissionByStagesType>();

    function getChartData() {
      const allModuleTypes = [
        ...new Set(
          props.materials.flatMap((m) => m.lciaResults.map((r) => r.moduleTypeEnum)).sort((a, b) => a - b)
        ),
      ].filter((n) => ![1, 2, 3].includes(n));

      const filteredStages =
        props.selectedStages !== undefined ? allModuleTypes.filter((n) => props.selectedStages!.includes(n)) : allModuleTypes;

      const data: InspectEmissionByStagesType = {};

      for (const material of props.materials) {
        const indicatorType = resolveIndicatorType(material, props.selectedIndicator);

        const stageEntries = filteredStages.map((number) => {
          const lciaResult = material.lciaResults.find((r) => r.moduleTypeEnum === number);
          const indicator = lciaResult?.indicators.find((i) => i.type === indicatorType);
          const label = getStageTooltip(number);
          return {
            id: label,
            label,
            value: indicator?.value ?? 0,
          };
        });

        const totalValue = stageEntries.reduce((sum, entry) => sum + entry.value, 0);
        const entries = [{ id: "Total", label: "Total", value: totalValue }, ...stageEntries];

        const name = localization.getEpdTranslation(material.names, material.name);
        data[name] = { name, entries };
      }

      chartData.value = data;
    }

    onMounted(() => getChartData());

    watch(() => props.materials, () => getChartData(), { deep: true });
    watch(() => props.selectedIndicator, () => getChartData());
    watch(() => props.selectedStages, () => getChartData(), { deep: true });

    return { chartData };
  },

  render() {
    const { chartData, chartTitle, indicatorUnit } = this;

    if (!chartData) return null;

    return (
      <EmissionByMaterialLifecycleStageChart
        data={chartData}
        unit={indicatorUnit}
        title={chartTitle}
        settings={{ rotateLabels: 45, legend: true, stackedCategories: ["Total"] }}
      />
    );
  },
});

export default CompareGraph;
